import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  normalizeHexColor,
  SalesforceIconService,
} from 'icon-service-lib';
import { Observable, of } from 'rxjs';

@Component({
  selector: 'app-salesforce-entity-icon',
  templateUrl: './salesforce-entity-icon.component.html',
  styleUrl: './salesforce-entity-icon.component.scss',
  imports: [CommonModule, MatTooltipModule],
})
export class SalesforceEntityIconComponent implements OnChanges {
  private static readonly SALESFORCE_ICON_FALLBACK_COLORS: Record<string, string> = {
    'brc-account': '#576ce3',
    'brc-application': '#CB65FF',
    'brc-contact': '#951bc3',
    'brc-job': '#46b5e4',
    'brc-closing-report': '#bf212c',
  };

  @Input() entityType: string | undefined;
  @Input() iconUrl: string | undefined;
  @Input() iconColor: string | undefined;
  @Input() iconClass: string | undefined;
  @Input() tooltip: string | undefined;
  @Input() imgClass: string = 'entity-icon-img';
  @Input() renderAsBackground = false;
  @Input() renderAsWatermark = false;
  @Input() watermarkRenderMode: 'auto' | 'class' | 'image' = 'auto';

  resolvedUrl$: Observable<string | null> = of(null);
  resolvedIconClass: string | null = null;

  constructor(private salesforceIconService: SalesforceIconService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['iconUrl']) {
      this.resolvedUrl$ = this.salesforceIconService.resolveIconUrl$(this.iconUrl);
    }
    if (changes['iconClass'] || changes['entityType']) {
      this.resolvedIconClass = this.resolveIconClass();
    }
  }

  onError(): void {
    this.salesforceIconService.markIconUrlFailed(this.iconUrl);
  }

  shouldRenderWatermarkAsClass(): boolean {
    if (!this.renderAsWatermark) {
      return false;
    }

    if (this.watermarkRenderMode === 'image') {
      return false;
    }

    if (!this.resolvedIconClass) {
      return false;
    }

    if (this.watermarkRenderMode === 'class') {
      return true;
    }

    return !this.resolvedIconClass.includes('brc-custom-object');
  }

  get watermarkClass(): string | null {
    if (!this.resolvedIconClass) {
      return null;
    }
    return `${this.resolvedIconClass} watermark`.trim();
  }

  get colorStyle(): { 'background-color': string } | null {
    const color = this.resolveIconColor();
    return color ? { 'background-color': color } : null;
  }

  getBackgroundStyle(url: string): Record<string, string> {
    const color = this.resolveIconColor() ?? '#d3d3d3';
    return {
      'background-image': `url('${url}')`,
      'background-size': '100% 100%',
      'background-repeat': 'no-repeat',
      'background-position': 'center',
      'background-color': color,
    };
  }

  private resolveIconClass(): string | null {
    const explicitIconClass = this.iconClass?.trim();
    if (explicitIconClass) {
      return explicitIconClass;
    }

    const entityType = this.entityType?.trim();
    if (!entityType) {
      return null;
    }

    return this.salesforceIconService.getIcon(entityType) || null;
  }

  private resolveIconColor(): string | null {
    const explicitColor = normalizeHexColor(this.iconColor);
    if (explicitColor) {
      return explicitColor;
    }

    const iconClass = this.resolvedIconClass ?? '';
    const fallbackEntry = Object.entries(SalesforceEntityIconComponent.SALESFORCE_ICON_FALLBACK_COLORS).find(([token]) =>
      iconClass.includes(token)
    );

    return fallbackEntry?.[1] ?? null;
  }
}
